/**
 * API functions for scenario management
 */

import axios from 'axios';
import type { MotionModel } from '../motion/model';
import { apiClient } from '../../services/apiClient';
import { waitForNextPoll } from '../../utils/polling';
import { notifyImportContentChanged, scenePayloadAffectsImportVerification } from './importReportRefresh';
import type { DialogueLine, ImportReport, SceneFrameMentionEntry, SceneFrameMentionMap, NarrationDialogueSwapDryRun, NarrationDialogueSwapRequest, NarrationDialogueSwapResult, Scene, Scenario, ScenarioCharacter, ScenarioListItem, ScenarioLocation, Storyboard, TelopLine, UpdateScenarioPayload, UpdateScenePayload } from './types';

/**
 * シーン系エンドポイントが返す `scenes` テーブルの生行。
 *
 * バックエンド (backend/services/scenario/crud/scenario_crud.py) は Supabase の行を
 * そのまま jsonify するため、正本の列名は `order_number`。
 * ただしブランドカット系 (product-last-cut / logo-last-cut / title-card) だけは
 * `_fetch_scene()` / `_create_brand_cut_scene()` がフロント互換のため
 * `order = order_number` のコピーを追加で載せて返す (ai-continue / create-scene には無い)。
 */
export type SceneRow = Scene & { order?: number };

/**
 * ブランドカット (タイトルカード / エンドカード / 商品カット) 生成エンドポイントの戻り値。
 *
 * 正常時は生成/置換されたシーン行で、この経路だけは `order` が必ず載る
 * (`_fetch_scene()` / `_create_brand_cut_scene()` が付与する)。
 *
 * 注意: ルート側には `result.skipped` を見て `{skipped, reason}` だけを返す旧分岐が残るが、
 * サービス実装 (scene_generator.py) から skip は撤去済みで現行では到達しない。
 * 実データに合わせて `skipped` / `reason` は optional のフラグとしてのみ表現し、
 * シーン行のフィールドは optional にしていない (再導入する場合は union へ組み替えること)。
 *
 * `frame_generated` 以降は product-last-cut に `auto_generate_assets` を渡したときだけ付く。
 */
export type BrandCutSceneResponse = Scene & {
  order: number;
  skipped?: boolean;
  reason?: string;
  frame_generated?: boolean;
  frame_error?: string;
  narration_generated?: boolean;
  narration_error?: string;
};

export interface ListScenariosParams {
  page?: number;
  limit?: number;
  filter?: 'all' | 'starred' | 'bookmarked' | 'published' | 'own';
  search?: string;
  asin?: string;
  product_id?: string;
  brand_id?: string;
  publish_filter?: '' | 'published' | 'unpublished';
  owner_filter?: '' | 'own' | 'others';
  has_images?: boolean;
  has_videos?: boolean;
  // 制作方式フィルタ (docs/features/scene-location-and-production-mode-plan.md §3.7)。
  // 絵コンテから制作 / シーンごとに制作 の2値。BE (list_scenarios) は旧値
  // 'frame'/'reference' を 'per_scene' へ読み替える互換を持つ。
  mode_filter?: 'storyboard' | 'per_scene';
  // アスペクト比 (scenarios.aspect_ratio と完全一致。'16:9' | '9:16' | '1:1' 等)
  aspect_ratio?: string;
  // 並び順。省略時は作成日時の新しい順 ('created_desc')
  sort?: ScenarioListSort;
  // 制作進捗フィルタ (organization_material_workflow_stages.id。複数は OR)。
  // no_workflow_stage=true で未設定を含める (ID と併用で「指定ステージ OR 未設定」)。
  // docs/features/workflow-stage-take8-and-scenario-book-plan.md §3.3.1
  workflow_stage_ids?: string[];
  no_workflow_stage?: boolean;
}

/** シナリオ一覧の並び順 (列 × 方向)。既定は 'created_desc' */
export type ScenarioListSort = 'created_desc' | 'created_asc' | 'updated_desc' | 'updated_asc';
export const DEFAULT_SCENARIO_LIST_SORT: ScenarioListSort = 'created_desc';

export interface ListScenariosResponse {
  items: ScenarioListItem[];
  total: number;
  page: number;
  limit: number;
}

/**
 * List scenarios for the current organization with pagination and filtering
 */
export async function listScenarios(params?: ListScenariosParams): Promise<ListScenariosResponse> {
  const queryParams = new URLSearchParams();

  if (params?.page) {
    queryParams.append('page', params.page.toString());
  }
  if (params?.limit) {
    queryParams.append('limit', params.limit.toString());
  }
  if (params?.filter && params.filter !== 'all') {
    queryParams.append('filter', params.filter);
  }
  if (params?.search) {
    queryParams.append('search', params.search);
  }
  if (params?.asin) {
    queryParams.append('asin', params.asin);
  }
  if (params?.product_id) {
    queryParams.append('product_id', params.product_id);
  }
  if (params?.brand_id) {
    queryParams.append('brand_id', params.brand_id);
  }
  if (params?.publish_filter) {
    queryParams.append('publish_filter', params.publish_filter);
  }
  if (params?.owner_filter) {
    queryParams.append('owner_filter', params.owner_filter);
  }
  if (params?.has_images) {
    queryParams.append('has_images', 'true');
  }
  if (params?.has_videos) {
    queryParams.append('has_videos', 'true');
  }
  if (params?.mode_filter) {
    queryParams.append('mode_filter', params.mode_filter);
  }
  if (params?.aspect_ratio) {
    queryParams.append('aspect_ratio', params.aspect_ratio);
  }
  if (params?.sort && params.sort !== DEFAULT_SCENARIO_LIST_SORT) {
    queryParams.append('sort', params.sort);
  }
  for (const id of params?.workflow_stage_ids ?? []) {
    queryParams.append('workflow_stage_ids', id);
  }
  if (params?.no_workflow_stage) {
    queryParams.append('no_workflow_stage', 'true');
  }

  const url = `/generation/scenarios${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  const { data } = await apiClient.get<ListScenariosResponse>(url);
  return data;
}

/** 取り込みレポート系 API の応答 (`{ import_report }`。import_report は完全な形で、verification.overall は再計算済み)。 */
type ImportReportResponse = { import_report: ImportReport };

/**
 * 台本取り込みレポートの「原文と照合しました」(利用者確認) を記録する。
 * 更新後の import_report を返す (docs/features/scenario-script-import-plan.md §6.6.7)。
 */
export async function confirmImportReport(scenarioId: string): Promise<ImportReport> {
  const { data } = await apiClient.post<ImportReportResponse>(`/generation/scenarios/${scenarioId}/import-report/confirm`);
  return data.import_report;
}

/** 利用者確認を取り消す (確認記録は confirmation_history へ移る)。更新後の import_report を返す。 */
export async function cancelImportReportConfirmation(scenarioId: string): Promise<ImportReport> {
  const { data } = await apiClient.delete<ImportReportResponse>(`/generation/scenarios/${scenarioId}/import-report/confirm`);
  return data.import_report;
}

/**
 * 最新の取り込みレポートを取得する (現在のシーン・登場人物から利用者確認の有効性を再判定した verification を含む)。
 * 取り込み由来でないシナリオは 400 になるので、呼び出し側で origin='imported' を確かめてから呼ぶ。
 */
export async function getImportReport(scenarioId: string): Promise<ImportReport> {
  const { data } = await apiClient.get<ImportReportResponse>(`/generation/scenarios/${scenarioId}/import-report`);
  return data.import_report;
}

/**
 * Get a specific scenario with its scenes
 */
export async function getScenario(scenarioId: string): Promise<Scenario> {
  const { data } = await apiClient.get<Scenario>(`/generation/scenarios/${scenarioId}`);
  return data;
}

/**
 * Update a scenario
 */
export async function updateScenario(scenarioId: string, payload: UpdateScenarioPayload): Promise<Scenario> {
  const { data } = await apiClient.put<Scenario>(`/generation/scenarios/${scenarioId}`, payload);
  return data;
}

/**
 * Update a scene
 */
/**
 * フレーム生成の参照画像の計画 (ドライラン)。候補+overrides から「添付予定」を返す
 * (docs/features/scene-frame-generation-tuning-plan.md §6.1)。生成ジョブと同じ BE 関数で解決する。
 */
export async function fetchSceneFrameReferencePlan(
  sceneId: string,
  input: { candidates: unknown[]; overrides: unknown; frame_type: 'first' | 'last' },
): Promise<{ plan: Record<string, unknown> }> {
  const { data } = await apiClient.post<{ plan: Record<string, unknown> }>(`/generation/scenes/${sceneId}/frame-reference-plan`, input);
  return data;
}

export async function updateScene(sceneId: string, payload: UpdateScenePayload): Promise<SceneRow> {
  const { data } = await apiClient.put<SceneRow>(`/generation/scenes/${sceneId}`, payload);
  // 台本取り込み: 素材の採用・生成物の列だけの更新では照合を失効させない (BEと同じ判定。importReportRefresh.ts)
  if (scenePayloadAffectsImportVerification(payload as Record<string, unknown>)) {
    notifyImportContentChanged({ scenarioId: data?.scenario_id ?? null, sceneIds: [sceneId] });
  }
  return data;
}

/**
 * Create a new scene in a scenario
 * after_scene_id を渡すと、そのシーンの直後に挿入される (後続シーンは backend で +1 採番)
 */
export async function createScene(scenarioId: string, payload: { setting?: string; visual?: string; script?: string; narration?: string; dialogue?: string; after_scene_id?: string }): Promise<SceneRow> {
  const { data } = await apiClient.post<SceneRow>(`/generation/scenarios/${scenarioId}/scenes`, payload);
  notifyImportContentChanged({ scenarioId });
  return data;
}

/**
 * 場面固有化 (P1) のシナリオ場面リストへ新規の場面を追加する。
 * `key` は呼び出し側 (FE) が生成した不変 ID (ユーザーには見せない)。
 * docs/features/scene-location-and-production-mode-plan.md §2.1/§2.3。
 */
export async function createScenarioLocation(
  scenarioId: string,
  payload: { key: string; name: string; description: string },
): Promise<{ locations: ScenarioLocation[] }> {
  const { data } = await apiClient.post<{ locations: ScenarioLocation[] }>(
    `/generation/scenarios/${scenarioId}/locations`,
    payload,
  );
  return data;
}

/** POST .../scenes/<scene_id>/frame-mentionの返却 (§2.3)。 */
export type SceneFrameMentionResponse = {
  frame_material_id: string;
  /** トークンのID部 (frame_material_idのハイフン除去・小文字・先頭4桁)。 */
  id4: string;
  /** 例: 「シーン1の最後のフレーム」 */
  label: string;
  thumbnail_url: string | null;
  position: 'first' | 'last';
  scene_id: string;
  scene_order: number;
  video_material_id: string;
  entry: SceneFrameMentionEntry;
  /** マージ後の記録map全体。FEはこれでローカルのgeneration_metadataを置き換える。 */
  scene_frame_mentions: SceneFrameMentionMap;
};

/**
 * 他シーンの動画の最初/最後のフレームを確保し、@参照の記録へ追加する
 * (docs/features/scene-video-frame-materials-visibility-plan.md §2.3)。
 * 素材IDは送らない (BEがシーンの現在の動画から抽出する)。上限超過等は400
 * (SCENE_FRAME_MENTION_LIMIT / _NO_VIDEO / _UNSUPPORTED_SCENE)。
 */
export async function createSceneFrameMention(
  scenarioId: string,
  sceneId: string,
  position: 'first' | 'last',
): Promise<SceneFrameMentionResponse> {
  const { data } = await apiClient.post<SceneFrameMentionResponse>(
    `/generation/scenarios/${scenarioId}/scenes/${sceneId}/frame-mention`,
    { position },
  );
  return data;
}

/** GET .../frame-mentionsの1件 (記録 + フレーム素材の表示用URLと有無)。 */
export type SceneFrameMentionWithThumbnail = SceneFrameMentionEntry & {
  /** フレーム素材自身の表示用URL (署名付き/公開URL)。missingならnull。 */
  thumbnail_url: string | null;
  /** フレーム素材が無い・削除済み・他組織なら true (チップは「参照先なし」)。 */
  missing: boolean;
};

export type SceneFrameMentionsResponse = {
  entries: Record<string, SceneFrameMentionWithThumbnail>;
};

/**
 * 記録の各フレームのチップ表示用データ。キーにフレーム素材IDの集合 (ソート済み)を含め、
 * 記録が増減したら取り直す (挿入成功時はsetQueryDataで新しいキーへ先に書く)。
 */
export const sceneFrameMentionsQueryKey = (scenarioId: string, frameMaterialIds: readonly string[]) => (
  ['scene-frame-mentions', scenarioId, [...frameMaterialIds].sort().join(',')] as const
);

/**
 * @メンションで参照中のシーンフレーム記録を、フレーム素材の表示用URLと有無付きで取得する
 * (docs/features/scene-video-frame-materials-visibility-plan.md §2.3。再読込後のチップ表示用)。
 */
export async function fetchSceneFrameMentions(scenarioId: string): Promise<SceneFrameMentionsResponse> {
  const { data } = await apiClient.get<SceneFrameMentionsResponse>(
    `/generation/scenarios/${scenarioId}/frame-mentions`,
  );
  return data;
}

/**
 * 場面の name / description を改名する（key は不変・削除 API はない）。
 */
export async function renameScenarioLocation(
  scenarioId: string,
  locationKey: string,
  payload: { name: string; description: string },
): Promise<{ locations: ScenarioLocation[] }> {
  const { data } = await apiClient.put<{ locations: ScenarioLocation[] }>(
    `/generation/scenarios/${scenarioId}/locations/${encodeURIComponent(locationKey)}`,
    payload,
  );
  return data;
}

/**
 * レガシーシーン (telop_lines が NULL) をフラット telop から推定した話者付き telop_lines で
 * 構造化モードへ昇格する (docs/features/scenario-telop-speaker-plan.md「シーン単位の昇格」)。
 * 既に構造化なら現在行がそのまま返る。
 */
export async function inferSceneTelopLines(sceneId: string): Promise<SceneRow> {
  const { data } = await apiClient.post<SceneRow>(`/generation/scenes/${sceneId}/telop-lines/infer`);
  notifyImportContentChanged({ scenarioId: data?.scenario_id ?? null, sceneIds: [sceneId] });
  return data;
}

/** 登場人物宣言 (generation_metadata.characters) の API 返却値 (docs/features/scenario-telop-speaker-plan.md §3.7)。 */
export type ScenarioCharactersResult = {
  characters: ScenarioCharacter[];
  /** 改名/削除で speaker が一括更新されたシーン (id と更新後の dialogue_lines / telop_lines)。 */
  updated_scenes: Array<{ id: string; dialogue_lines: DialogueLine[] | null; telop_lines: TelopLine[] | null }>;
};

export type ScenarioCharacterPayload = { label: string; description?: string; appears_on_screen?: boolean };

export async function createScenarioCharacter(
  scenarioId: string,
  payload: ScenarioCharacterPayload & { key: string },
): Promise<ScenarioCharactersResult> {
  const { data } = await apiClient.post<ScenarioCharactersResult>(
    `/generation/scenarios/${scenarioId}/characters`,
    payload,
  );
  notifyImportContentChanged({ scenarioId });
  return data;
}

/** 登場人物の label / description / appears_on_screen を更新する (key は不変。参照行の label も BE が一括更新)。 */
export async function renameScenarioCharacter(
  scenarioId: string,
  characterKey: string,
  payload: ScenarioCharacterPayload,
): Promise<ScenarioCharactersResult> {
  const { data } = await apiClient.put<ScenarioCharactersResult>(
    `/generation/scenarios/${scenarioId}/characters/${encodeURIComponent(characterKey)}`,
    payload,
  );
  notifyImportContentChanged({ scenarioId });
  return data;
}

/** 登場人物宣言を削除する (参照行は character_key が外れ自由ラベルに戻る。行は消えない)。 */
export async function deleteScenarioCharacter(
  scenarioId: string,
  characterKey: string,
): Promise<ScenarioCharactersResult> {
  const { data } = await apiClient.delete<ScenarioCharactersResult>(
    `/generation/scenarios/${scenarioId}/characters/${encodeURIComponent(characterKey)}`,
  );
  notifyImportContentChanged({ scenarioId });
  return data;
}

/**
 * Delete a scenario
 */
export async function deleteScenario(scenarioId: string): Promise<void> {
  await apiClient.delete(`/generation/scenarios/${scenarioId}`);
}

/**
 * Delete a scene
 */
export async function deleteScene(sceneId: string): Promise<void> {
  await apiClient.delete(`/generation/scenes/${sceneId}`);
  notifyImportContentChanged({ sceneIds: [sceneId] });
}

// ============================================================
// シーンのナレーション / 動画 / BGM
// (components/generation/scenario 配下から集約。URL・payload・戻り値は移動前のまま)
// ============================================================

/** TTS ボイス */
export interface TtsVoice {
  voice_id: string;
  name: string;
  [key: string]: unknown;
}

/**
 * ボイス一覧の絞り込み (API 側で絞るプロバイダ = Fish Audio 向け)。
 * gender / age は VoiceSelect のラベル値 ('female' / 'middle_aged' 等)。
 * licensed は Fish Official の権利処理済みボイスのみ。
 * ElevenLabs / Gemini はサーバー側が無視して全件を返す (フロントで絞る)。
 */
export interface TtsVoiceFilters {
  gender?: string;
  age?: string;
  licensed?: boolean;
}

/**
 * TTS ボイス一覧をプロバイダ別に取得する。
 *
 * ボイスの詳細フィールド (labels 等) は呼び出し側で使う形が異なるため、
 * 要素型を型引数で指定できるようにしてある。
 */
export async function listTtsVoices<T = TtsVoice>(provider: string, filters?: TtsVoiceFilters): Promise<T[]> {
  const params = new URLSearchParams({ provider });
  if (filters?.gender) params.set('gender', filters.gender);
  if (filters?.age) params.set('age', filters.age);
  if (filters?.licensed) params.set('licensed', '1');
  const { data } = await apiClient.get<{ voices: T[] }>(
    `/generation/voices?${params.toString()}`,
  );
  return data.voices;
}

/** プロバイダ (の今の既定モデル・経路) で使えるナレーション機能。BE の narration_capabilities と同じ形 */
export interface NarrationCapabilities {
  /** 一括生成の「読み方」を適用できるか (Gemini は 3.8 経路のときだけ true) */
  narration_styles: boolean;
  /** 「英語で発音する」を適用できるか */
  english_pronunciation: boolean;
  /** ボイス一覧が性別・年代を持ち、ナレーターの絞り込みが効くか (Gemini は 3.8 経路のライブラリの声のとき true)。
   *  旧 BE は返さない */
  voice_filters?: boolean;
}

/**
 * ナレーション機能の対応可否を取得する (ボイス一覧は取らない軽量 API)。
 * Gemini は BE の GEMINI_TTS_MODEL の切り替え (3.8 ⇔ 3.1 旧経路) で対応が変わるため、
 * provider 単位の定数で決め打ちせずこの値で出し分ける。
 */
export async function getNarrationCapabilities(provider: string): Promise<NarrationCapabilities> {
  const { data } = await apiClient.get<{ capabilities: NarrationCapabilities }>(
    `/generation/narration-capabilities?provider=${encodeURIComponent(provider)}`,
  );
  return data.capabilities;
}

/**
 * TTS ボイス 1 件を取得する。存在しない / 1 件取得に対応していないプロバイダは null。
 *
 * Fish Audio の一覧は絞り込み条件ごとのスコア上位 100 件しか返さないため、絞り込み経由で
 * 選んで保存した声が無条件の一覧に無いことがある。保存済みシナリオの声の復元に使う。
 */
export async function getTtsVoice<T = TtsVoice>(provider: string, voiceId: string): Promise<T | null> {
  try {
    const { data } = await apiClient.get<{ voice: T }>(
      `/generation/voices/${encodeURIComponent(voiceId)}?provider=${provider}`,
    );
    return data.voice ?? null;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) return null;
    throw error;
  }
}

/**
 * シーンのナレーション履歴を取得する (古い順のまま返す。反転は呼び出し側)。
 */
export async function listSceneNarrations(sceneId: string): Promise<any[]> {
  const { data } = await apiClient.get(`/generation/scenes/${sceneId}/narrations`);
  return data.narrations || [];
}

/**
 * シーンに紐付けるナレーション素材を切り替える。
 */
export async function selectSceneNarration(sceneId: string, materialId: string): Promise<void> {
  await apiClient.put(`/generation/scenes/${sceneId}/narration`, { material_id: materialId });
}

/**
 * シーンの動画とナレーション音声を合成する。
 * 戻り値は `{ material }` 等を含む生レスポンス。
 */
export async function mergeSceneNarration(sceneId: string): Promise<any> {
  const { data } = await apiClient.post(`/generation/scenes/${sceneId}/merge-narration`);
  return data;
}

/**
 * シーンの動画履歴 (テイク) を取得する (古い順のまま返す。反転は呼び出し側)。
 * 動画 AI のテイクとシーンごとのモーション素材 (engine='motion') を返す。
 * `kind: 'motion_overlay'` は絵コンテのモーション上書きの過去版だけ (is_current = 今の motion_overlay)。
 */
export async function listSceneVideos(sceneId: string, opts?: { kind?: 'motion_overlay' }): Promise<any[]> {
  const { data } = await apiClient.get(`/generation/scenes/${sceneId}/videos`, {
    params: opts?.kind ? { kind: opts.kind } : undefined,
  });
  return data.videos || [];
}

// ── 絵コンテのシーン区間 (境界) とモーション上書き (§10.13.1 契約 B / C の API) ──

/** `GET /api/generation/scenarios/<sid>/storyboard/spans` の 1 シーン分の区間。 */
export interface StoryboardSpanRow {
  scene_id: string;
  in_frame: number;
  out_frame: number;
  in_sec: number;
  out_sec: number;
  /** span_override が適用された。 */
  overridden: boolean;
  /** span_override があったが版が合わず無視した (「絵コンテ動画が変わったので境界を確認してください」)。 */
  boundary_stale: boolean;
}

/** 同 API の 1 シーン分のモーション上書き (storyboardSpans.resolveMotionOverlay の BE 版の結果)。 */
export interface StoryboardMotionOverlayRow {
  scene_id: string;
  material_id: string;
  in_frame: number;
  out_frame: number;
  fit: 'exact' | 'trim' | 'freeze' | 'stale';
  reason: 'base_material_changed' | 'duration_changed' | 'duration_adjusted' | null;
  mix_sound: boolean;
  show_telop: boolean;
  generated_duration_sec: number;
}

/** 同 API の担当カード 1 枚分。 */
export interface StoryboardSpansCard {
  card_scene_id: string;
  base_material_id: string;
  card_range: { start: number; end: number };
  fps: { num: number; den: number };
  segment_frames: number;
  duration_sec: number;
  spans: StoryboardSpanRow[];
  motion_overlays: StoryboardMotionOverlayRow[];
}

export interface StoryboardSpansResponse {
  cards: StoryboardSpansCard[];
}

/**
 * 絵コンテの担当カードごとのシーン区間 (span_override 適用済み) とモーション上書きの鮮度を読む
 * (ポイント不要。BE が素材の duration_seconds / video_fps を優先し、無ければ ffprobe して素材行に書き戻す)。
 */
export async function fetchStoryboardSpans(scenarioId: string): Promise<StoryboardSpansResponse> {
  const { data } = await apiClient.get<StoryboardSpansResponse>(`/generation/scenarios/${scenarioId}/storyboard/spans`);
  return data;
}

export interface StoryboardBoundariesPayload {
  card_scene_id: string;
  boundaries: Array<{ scene_id: string; in_frame: number; out_frame: number }>;
}

/**
 * 境界調整の保存 `PUT /api/generation/scenarios/<sid>/storyboard/boundaries` (隣接の共有境界は BE が同期して書く)。
 * 応答は fetchStoryboardSpans と同じ形。
 */
export async function putStoryboardBoundaries(
  scenarioId: string,
  payload: StoryboardBoundariesPayload,
): Promise<StoryboardSpansResponse> {
  const { data } = await apiClient.put<StoryboardSpansResponse>(
    `/generation/scenarios/${scenarioId}/storyboard/boundaries`,
    payload,
  );
  return data;
}

/** 絵コンテのモーション上書き (motion_overlay) の付け外し・効果音・テロップ表示の更新本文 (省略したキーは変えない)。 */
export interface SceneMotionOverlayUpdatePayload {
  /** 「利用しない」(true = 元の映像に戻る。生成結果は保持)。 */
  disabled?: boolean;
  /** 「モーションの効果音」(元の音声に重ねる)。 */
  mix_sound?: boolean;
  /** 「テロップを表示」(既定 false = 上書き区間のテロップを隠す)。 */
  show_telop?: boolean;
  /** このシーンで過去に生成した上書き素材に差し替える (履歴から選び直す。「利用しない」は解除される)。 */
  material_id?: string;
}

/** `POST /api/generation/scenes/<id>/motion-overlay` の応答 (BE `scenario_storyboard_spans.post_scene_motion_overlay`)。 */
export interface SceneMotionOverlayUpdateResponse {
  scene_id: string;
  /** 併合後の `scenes.generation_metadata.motion_overlay` (生の形。`parseMotionOverlay` で読む)。 */
  motion_overlay: Record<string, unknown> | null;
}

/**
 * `POST /api/generation/scenes/<id>/motion-overlay` (§10.13 4. / 6.)。ポイント不要。
 * 応答は `{scene_id, motion_overlay}` (spans の形ではない)。呼び出し側はシーンの metadata を取り直し、spans を無効化する。
 */
export async function updateSceneMotionOverlay(
  sceneId: string,
  payload: SceneMotionOverlayUpdatePayload,
): Promise<SceneMotionOverlayUpdateResponse | null> {
  const { data, status } = await apiClient.post<SceneMotionOverlayUpdateResponse | undefined>(
    `/generation/scenes/${sceneId}/motion-overlay`,
    payload,
  );
  if (status === 204 || !data || typeof data.scene_id !== 'string') return null;
  return data;
}

/** `PUT /generation/scenes/<id>/video` で素材に合わせて書き換えたシーンの値 (モーション素材を選んだときだけ)。 */
export interface SelectSceneVideoScenePatch {
  generation_metadata?: Record<string, unknown>;
  video_duration_seconds?: number;
  video_prompt?: string;
}

/**
 * シーンに紐付ける動画素材を切り替える。
 * モーション素材を選ぶと backend がシーンの generation_metadata / 尺を素材の生成時の値に揃え、その値を返す (動画 AI のテイクは null)。
 */
export async function selectSceneVideo(sceneId: string, materialId: string): Promise<SelectSceneVideoScenePatch | null> {
  const { data } = await apiClient.put(`/generation/scenes/${sceneId}/video`, { material_id: materialId });
  return (data && typeof data === 'object' && data.scene) || null;
}

/**
 * シナリオの BGM 履歴を取得する。
 */
export async function listScenarioBgmHistory(scenarioId: string): Promise<any[]> {
  const { data } = await apiClient.get(`/generation/scenarios/${scenarioId}/bgm-history`);
  return data.bgm_history || [];
}

/**
 * 公開前の採用素材移行プレフライト結果
 */
export interface PublishPreflightResult {
  scenario_id: string;
  total: number;
  migratable: string[];
  already_shared: string[];
  partially_migrated: string[];
  missing: string[];
  /** 移行対象(migratable + partially_migrated)の種別ごとの件数。
   *  キー: video / image / narration / sound_logo / bgm / storyboard */
  breakdown?: Record<string, number>;
}

/**
 * 採用素材移行のサマリ (公開時バッチの結果)
 */
export interface AssetMigrationSummary {
  scenario_id: string;
  migrated: string[];
  recovered: string[];
  already_shared: string[];
  missing: string[];
  failed: Array<{ id: string; message?: string }>;
}

/**
 * 公開前に採用素材の移行対象を取得する (move はしない)
 */
export async function preflightPublishScenario(scenarioId: string): Promise<PublishPreflightResult> {
  const { data } = await apiClient.post<PublishPreflightResult>(
    `/generation/scenarios/${scenarioId}/publish/preflight`,
  );
  return data;
}

/**
 * Publish a scenario to make it visible to the organization.
 *
 * migrateAssets=true のとき採用素材を共有フォルダへ移行し、以後の継続移行を有効化する。
 * 後方互換のためレスポンスはシナリオ dict をトップレベルに持ち、migration を添える。
 */
export async function publishScenario(
  scenarioId: string,
  opts?: { migrateAssets?: boolean },
): Promise<Scenario & { migration?: AssetMigrationSummary | null }> {
  const query = opts?.migrateAssets ? '?migrate_assets=1' : '';
  const { data } = await apiClient.post<Scenario & { migration?: AssetMigrationSummary | null }>(
    `/generation/scenarios/${scenarioId}/publish${query}`,
  );
  return data;
}

/**
 * シナリオを複製する (自分のシナリオのみ。シーンと採用中の絵コンテをコピー)
 */
export async function duplicateScenario(scenarioId: string): Promise<{ id: string; title: string }> {
  const { data } = await apiClient.post<{ success: boolean; scenario: { id: string; title: string } }>(
    `/generation/scenarios/${scenarioId}/duplicate`,
  );
  return data.scenario;
}

/**
 * Unpublish a scenario to make it private to the creator
 */
export async function unpublishScenario(scenarioId: string): Promise<Scenario> {
  const { data } = await apiClient.post<Scenario>(`/generation/scenarios/${scenarioId}/unpublish`);
  return data;
}

/**
 * Mark a scenario as starred (favorite) by its creator
 */
export async function starScenario(scenarioId: string): Promise<Scenario> {
  const { data } = await apiClient.post<{ success: boolean; starred: boolean; scenario: Scenario }>(
    `/generation/scenarios/${scenarioId}/star`
  );
  return data.scenario;
}

/**
 * Remove starred (favorite) mark from a scenario
 */
export async function unstarScenario(scenarioId: string): Promise<Scenario> {
  const { data } = await apiClient.delete<{ success: boolean; starred: boolean; scenario: Scenario }>(
    `/generation/scenarios/${scenarioId}/star`
  );
  return data.scenario;
}

/**
 * Add a bookmark to a scenario
 */
export async function bookmarkScenario(scenarioId: string): Promise<void> {
  await apiClient.post(`/generation/scenarios/${scenarioId}/bookmark`);
}

/**
 * Remove a bookmark from a scenario
 */
export async function unbookmarkScenario(scenarioId: string): Promise<void> {
  await apiClient.delete(`/generation/scenarios/${scenarioId}/bookmark`);
}

/**
 * Scenario prompt option
 */
export interface ScenarioPromptOption {
  id: string;
  value: string;
  label: string;
  description?: string;
  display_order: number;
  group_id?: string | null;
}

/**
 * Scenario prompt option group (parent category)
 */
export interface ScenarioPromptOptionGroup {
  id: string;
  label: string;
  display_order: number;
  children: ScenarioPromptOption[];
}

/**
 * Scenario prompt options grouped by category
 */
export interface ScenarioPromptOptionsResponse {
  [category: string]: {
    label: string;
    options: ScenarioPromptOption[];
    groups?: ScenarioPromptOptionGroup[];
  };
}

/**
 * Get scenario prompt options (tone, duration, etc.) grouped by category
 */
export async function getScenarioPromptOptions(): Promise<ScenarioPromptOptionsResponse> {
  const { data } = await apiClient.get<ScenarioPromptOptionsResponse>('/generation/scenario-prompt-options');
  return data;
}

/**
 * シナリオ属性ツリー（映像スタイル等）。effective tree（共通＋自組織 OEM、可視性/override 適用済み）。
 * 指示文（prompt_text / prompt_text_contexts）はサーバ側で除去済み。
 * categories_enabled は新ツリー経路に切替済みのカテゴリ（フォームの DB 駆動切替判定に使う）。
 */
export interface ScenarioAttributeNode {
  id: string;
  key: string;
  label: string;
  node_type: 'group' | 'option' | 'numeric';
  metadata?: Record<string, unknown> | null;
  placement?: 'primary' | 'advanced';
  prompt_target?: string | null;
  section?: string | null;
  section_order?: number | null;
  sort_order?: number;
  is_prompt_prefix?: boolean;
  children?: ScenarioAttributeNode[];
}

export interface ScenarioAttributesResponse {
  items: ScenarioAttributeNode[];
  categories_enabled: string[];
}

export async function getScenarioAttributes(): Promise<ScenarioAttributesResponse> {
  const { data } = await apiClient.get<ScenarioAttributesResponse>('/generation/scenario-attributes');
  return data;
}

/**
 * BGM 生成プロバイダ。
 * lyria = Lyria Clip (30秒固定) / lyria-pro = Lyria Pro (フル曲・尺と展開をプロンプト制御)
 */
export type BgmProvider = 'elevenlabs' | 'lyria' | 'lyria-pro';


/**
 * BGM prompt (Gemini 生成 or ユーザー編集済み) — UI で編集する対象。
 * v2 フィールド (genre 以降) は optional で、全プロバイダでプロンプトに織り込まれる。
 * 旧形式 (v1) の localStorage 下書きもそのままパースできる (旧下書きに残る sections は無視)。
 */
export interface BgmPrompt {
  song_description: string;
  is_instrumental: boolean;
  inspiration_tags: string[];
  song_title: string;
  genre?: string;
  mood?: string;
  bpm?: number | null;
  musical_key?: string;
  duration_seconds?: number;
  /** 歌詞モードの歌手プロファイル (英語1文)。歌詞モード生成のレスポンスにのみ含まれる。
   *  docs/features/scenario-song-lyrics-bgm-plan.md §5.2.3 */
  singer_profile?: string;
}

/**
 * BGM generation response
 */
export interface BgmGenerationResponse {
  material_id: string;
  preview_url: string | null;
  scenario_id: string;
  /** テンプレート生成 (prompt_source='template') は本文非開示のため song_description 等が無い。 */
  bgm_prompt: Partial<BgmPrompt> & {
    prompt_source?: 'gemini' | 'user_override' | 'default_template' | 'template';
    template_key?: string;
    template_label?: string;
  };
  music_length_ms: number;
  /**
   * 歌詞モードのみ: 前奏の自動トリム結果 (backend bgm_intro_trim.py)。status='trimmed' のとき
   * trimmed_sec 秒ぶん先頭を切り落として保存済み (music_length_ms はトリム後の実尺)。
   */
  intro_trim?: { status: 'trimmed' | 'skipped' | 'failed'; trimmed_sec: number | null };
  /**
   * 歌詞モードのみ: 歌 → 映像の尺同期 (backend bgm_lyrics_sync.py・計画 md §5.2.5)。
   * status='applied' のとき scenes[].seconds が各シーンのコマ尺 (storyboard_duration_seconds)
   * として DB に書き戻し済み。FE はこれでローカル state をパッチする (再取得しない)。
   */
  lyrics_sync?: LyricsSyncResult;
}

/** 歌 → 映像の尺同期の結果 (BGM 生成レスポンスと再同期 API で共通)。 */
export interface LyricsSyncResult {
  status: 'applied' | 'skipped' | 'failed';
  reason: string;
  total_seconds: number;
  scene_count: number;
  matched_count: number;
  scenes: Array<{ scene_id: string; order_number: number | null; seconds: number }>;
}

/**
 * 採用中の歌詞モード BGM を解析し直し、歌に合わせてシーンのコマ尺を書き戻す
 * (POST /scenarios/:id/lyrics-sync・BGM 再生成なし・クレジット消費なし)。
 * 前提違反 (採用 BGM 無し / 歌でない / ストーリーボード以外) は 400 で error メッセージが返る。
 */
export async function syncScenarioLyricsTiming(
  scenarioId: string,
): Promise<{ material_id: string; scenario_id: string; whisper_seconds: number; lyrics_sync: LyricsSyncResult }> {
  const { data } = await apiClient.post(`/generation/scenarios/${scenarioId}/lyrics-sync`, {});
  return data;
}

/**
 * Gemini で BGM プロンプトだけ取得する (音楽生成なし・クレジット消費なし)。
 * 「プロンプト編集」ボタン押下時に呼び、返ってきた内容をユーザーに提示・編集させる。
 */
export async function fetchScenarioBgmPrompt(
  scenarioId: string,
  provider?: BgmProvider,
  useStoryboardReference?: boolean,
): Promise<BgmPrompt> {
  const body: Record<string, unknown> = {};
  if (provider) body.provider = provider;
  if (typeof useStoryboardReference === 'boolean') {
    body.use_storyboard_reference = useStoryboardReference;
  }
  const { data } = await apiClient.post<{ bgm_prompt: BgmPrompt }>(
    `/generation/scenarios/${scenarioId}/bgm-prompt`,
    Object.keys(body).length > 0 ? body : undefined,
  );
  return data.bgm_prompt;
}

export interface GenerateScenarioBgmOptions {
  promptOverride?: BgmPrompt | null;
  /** BGM テンプレート (属性ツリー option key)。指定時は promptOverride を送らない
   *  (サーバー側で同時指定は 400)。本文はサーバー側でのみ解決される。 */
  bgmTemplateKey?: string | null;
  durationSeconds?: number | null;
  provider?: BgmProvider;
  /** ストーリーボードを参照画像として使うか (Lyria 系のみ作用。バックエンド既定は true) */
  useStoryboardReference?: boolean;
  /** 歌の有無 (全プロバイダ横断)。true=インスト (歌なし) / false=ボーカルあり。
   *  指定時は promptOverride / Gemini より優先される。 */
  isInstrumental?: boolean;
  /** 歌詞モード (docs/features/scenario-song-lyrics-bgm-plan.md §3.3)。true のとき
   *  provider は lyria-pro 固定・bgmTemplateKey / durationSeconds と同時指定不可 (400)。
   *  サーバーはシナリオの bgm_mode から推定しない (§3.6)。 */
  lyricsMode?: boolean;
  /** 歌詞モードの歌手プロファイル (任意・200 字上限はサーバー側検証)。 */
  singerProfile?: string | null;
}

/**
 * Generate BGM audio for a scenario (async job).
 * Submits a background job and polls until completion.
 *
 * promptOverride を渡すと Gemini をスキップして、その内容をそのまま使う。
 * useStoryboardReference=true (Lyria 系のみ) はストーリーボードの雰囲気・色味を曲に反映する。
 */
export async function generateScenarioBgm(
  scenarioId: string,
  options: GenerateScenarioBgmOptions = {},
): Promise<BgmGenerationResponse> {
  const {
    promptOverride, bgmTemplateKey, durationSeconds, provider, useStoryboardReference, isInstrumental,
    lyricsMode, singerProfile,
  } = options;
  const body: Record<string, unknown> = {};
  // 歌詞モードは bgm_template_key / duration_seconds と同時指定不可 (サーバー 400)。
  // 尺は想定尺で決まるため送らない (§5.2.1)。
  if (!lyricsMode) {
    if (bgmTemplateKey) body.bgm_template_key = bgmTemplateKey;
    else if (promptOverride) body.prompt_override = promptOverride;
    if (typeof durationSeconds === 'number' && durationSeconds > 0) {
      body.duration_seconds = Math.round(durationSeconds);
    }
  } else if (promptOverride) {
    body.prompt_override = promptOverride;
  }
  if (provider) body.provider = provider;
  if (typeof useStoryboardReference === 'boolean') {
    body.use_storyboard_reference = useStoryboardReference;
  }
  if (lyricsMode) {
    body.lyrics_mode = true;
    // is_instrumental は歌詞モードでは無視されサーバー側で常に false になるため送らない。
  } else if (typeof isInstrumental === 'boolean') {
    body.is_instrumental = isInstrumental;
  }
  if (typeof singerProfile === 'string' && singerProfile.trim()) {
    body.singer_profile = singerProfile.trim();
  }
  const { data: jobData } = await apiClient.post<{ job_id: string }>(
    `/generation/scenarios/${scenarioId}/bgm-async`,
    body,
  );
  const jobId = jobData.job_id;

  const POLL_INTERVAL = 3000;
  // Lyria Pro はフル曲生成で時間がかかるためポーリング上限を 10 分に拡大
  const MAX_POLLS = provider === 'lyria-pro' ? 200 : 60;
  for (let i = 0; i < MAX_POLLS; i++) {
    await waitForNextPoll(POLL_INTERVAL);
    const { data: job } = await apiClient.get<{
      status: string;
      result_data: BgmGenerationResponse | null;
      error_message: string | null;
    }>(`/jobs/${jobId}`);

    if (job.status === 'completed' && job.result_data) {
      return job.result_data;
    }
    if (job.status === 'failed') {
      throw new Error(job.error_message || 'BGM生成に失敗しました');
    }
  }
  throw new Error('BGM生成がタイムアウトしました。完了後にBGM履歴へ反映される場合があります');
}

/** 歌詞プレビューのシーン単位タイムライン 1 件 (docs/features/scenario-song-lyrics-bgm-plan.md §5.5)。 */
export interface LyricsPreviewTimelineItem {
  scene_id: string;
  order_number: number | null;
  start_sec: number;
  duration_sec: number;
  label: string | null;
  lines: string[];
}

export type LyricsPreviewResponse =
  | {
      ok: true;
      target_total_sec: number;
      song_total_sec: number;
      timeline: LyricsPreviewTimelineItem[];
      lyrics_text: string;
    }
  | { ok: false; reason: string };

/**
 * 歌詞モードのプレビュー (課金なし)。前提違反 (start ブランドカットあり / 先頭シーンに歌詞
 * 無し / 歌詞ゼロ / 想定尺未確定・30 秒未満など) は `{ok: false, reason}` で返る (200)。
 */
export async function fetchScenarioLyricsPreview(scenarioId: string): Promise<LyricsPreviewResponse> {
  const { data } = await apiClient.get<LyricsPreviewResponse>(
    `/generation/scenarios/${scenarioId}/lyrics-preview`,
  );
  return data;
}

/**
 * Brand logo status for a scenario
 */
export interface BrandLogoStatus {
  has_logo: boolean;
  brand_id?: string;
  brand_name?: string;
}

/**
 * Check if the brand associated with a scenario has logos
 */
export async function checkScenarioBrandLogo(scenarioId: string): Promise<BrandLogoStatus> {
  const { data } = await apiClient.get<BrandLogoStatus>(
    `/generation/scenarios/${scenarioId}/brand-logo-status`
  );
  return data;
}

/**
 * Generate the next scene using AI based on existing scenes
 */
export async function generateAiContinueScene(scenarioId: string, model?: string): Promise<SceneRow> {
  const { data } = await apiClient.post<SceneRow>(
    `/generation/scenarios/${scenarioId}/scenes/ai-continue`,
    model ? { model } : undefined
  );
  notifyImportContentChanged({ scenarioId });
  return data;
}

/**
 * Generate a product-focused last cut scene
 */
export async function generateProductLastCut(scenarioId: string, model?: string, force?: boolean): Promise<BrandCutSceneResponse> {
  const body: Record<string, any> = {};
  if (model) body.model = model;
  if (force) body.force = true;
  const { data } = await apiClient.post<BrandCutSceneResponse>(
    `/generation/scenarios/${scenarioId}/scenes/product-last-cut`,
    Object.keys(body).length > 0 ? body : undefined
  );
  notifyImportContentChanged({ scenarioId });
  return data;
}

/**
 * Generate a logo-only last cut scene
 */
export async function generateLogoLastCut(scenarioId: string, model?: string, force?: boolean): Promise<BrandCutSceneResponse> {
  const body: Record<string, any> = {};
  if (model) body.model = model;
  if (force) body.force = true;
  const { data } = await apiClient.post<BrandCutSceneResponse>(
    `/generation/scenarios/${scenarioId}/scenes/logo-last-cut`,
    Object.keys(body).length > 0 ? body : undefined
  );
  notifyImportContentChanged({ scenarioId });
  return data;
}

/**
 * Generate a logo-only title card (opening brand cut) scene
 */
export async function generateTitleCard(scenarioId: string, model?: string): Promise<BrandCutSceneResponse> {
  const body: Record<string, any> = {};
  if (model) body.model = model;
  const { data } = await apiClient.post<BrandCutSceneResponse>(
    `/generation/scenarios/${scenarioId}/scenes/title-card`,
    Object.keys(body).length > 0 ? body : undefined
  );
  notifyImportContentChanged({ scenarioId });
  return data;
}

/**
 * Attach a brand cut (title card / endcard) from an existing material (no AI generation, no charge)
 */
export async function attachBrandCutFromMaterial(
  scenarioId: string,
  params: { position: 'start' | 'end'; kind: 'logo' | 'product'; imageUrl: string; materialId?: string },
): Promise<any> {
  const { data } = await apiClient.post(
    `/generation/scenarios/${scenarioId}/scenes/brand-cut-from-material`,
    {
      position: params.position,
      kind: params.kind,
      image_url: params.imageUrl,
      material_id: params.materialId,
    },
  );
  notifyImportContentChanged({ scenarioId });
  return data;
}

/**
 * Get product-brand linking info for a scenario
 */
export interface ProductBrandInfo {
  product_type: 'amazon' | 'manual' | null;
  product_id: string | null;
  product_name: string | null;
  current_brand_id: string | null;
  current_brand_name: string | null;
}

export async function getScenarioProductBrandInfo(scenarioId: string): Promise<ProductBrandInfo> {
  const { data } = await apiClient.get<ProductBrandInfo>(
    `/generation/scenarios/${scenarioId}/product-brand-info`
  );
  return data;
}

/**
 * Link a product to a brand
 */
export async function linkProductToBrand(
  brandId: string,
  productType: 'amazon' | 'manual',
  productId: string,
): Promise<void> {
  await apiClient.post(`/brands/${brandId}/link-product`, {
    product_type: productType,
    product_id: productId,
  });
}

/**
 * Link a scenario to a brand (update scenarios.brand_id)
 */
export async function linkScenarioBrand(scenarioId: string, brandId: string): Promise<void> {
  await apiClient.post(`/brands/${brandId}/link-scenario`, {
    scenario_id: scenarioId,
  });
}

/**
 * Swap the order of two scenes
 */
export async function swapSceneOrder(sceneIdA: string, sceneIdB: string): Promise<{ success: boolean }> {
  const { data } = await apiClient.post<{ success: boolean }>('/generation/scenes/swap-order', {
    scene_id_a: sceneIdA,
    scene_id_b: sceneIdB,
  });
  notifyImportContentChanged({ sceneIds: [sceneIdA, sceneIdB] });
  return data;
}

/**
 * List soft-deleted scenarios (trash)
 */
export async function listDeletedScenarios(params?: { page?: number; limit?: number }): Promise<ListScenariosResponse> {
  const queryParams = new URLSearchParams();
  if (params?.page) queryParams.append('page', params.page.toString());
  if (params?.limit) queryParams.append('limit', params.limit.toString());
  const url = `/generation/scenarios/trash${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  const { data } = await apiClient.get<ListScenariosResponse>(url);
  return data;
}

/**
 * Restore a soft-deleted scenario
 */
export async function restoreScenario(scenarioId: string): Promise<void> {
  await apiClient.post(`/generation/scenarios/${scenarioId}/restore`);
}

/**
 * Permanently delete a soft-deleted scenario
 */
export async function permanentlyDeleteScenario(scenarioId: string): Promise<void> {
  await apiClient.delete(`/generation/scenarios/${scenarioId}/permanent`);
}

/**
 * Permanently delete multiple soft-deleted scenarios
 */
export async function bulkDeleteScenarios(ids: string[]): Promise<{ deleted_count: number; failed_ids: string[] }> {
  const { data } = await apiClient.post<{ deleted_count: number; failed_ids: string[] }>(
    '/generation/scenarios/trash/bulk-delete',
    { ids }
  );
  return data;
}

/**
 * Export scenario as a single composed MP4 video.
 * Returns the MP4 blob for download.
 */
export async function exportScenarioMp4(
  scenarioId: string,
  options?: { burn_telop?: boolean },
): Promise<Blob> {
  const { data } = await apiClient.post(
    `/generation/scenarios/${scenarioId}/export-mp4`,
    options || {},
    { responseType: 'blob', timeout: 600000 },  // 10 min timeout for large videos
  );
  return data;
}

/**
 * Export scenario as MP4 with streaming progress updates.
 * Streams NDJSON progress lines, then raw MP4 binary data.
 */
export async function exportScenarioMp4Stream(
  scenarioId: string,
  options?: { burn_telop?: boolean },
  onProgress?: (message: string) => void,
): Promise<Blob> {
  const baseURL = apiClient.defaults.baseURL || '/api';
  const token = (await import('../../services/apiClient')).getStoredToken();

  const response = await fetch(`${baseURL}/generation/scenarios/${scenarioId}/export-mp4-stream`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(options || {}),
  });

  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage = `Export failed with status ${response.status}`;
    try {
      const errorData = JSON.parse(errorText);
      errorMessage = errorData.error || errorMessage;
    } catch { /* ignore */ }
    throw new Error(errorMessage);
  }

  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error('Response body is not readable');
  }

  // Read NDJSON progress lines, then binary MP4 data
  let buffer = new Uint8Array(0);
  let headerComplete = false;
  let mp4Chunks: Uint8Array[] = [];
  let mp4BytesRead = 0;
  const decoder = new TextDecoder();

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    if (headerComplete) {
      // Already past NDJSON header, collect MP4 binary data
      mp4Chunks.push(value);
      mp4BytesRead += value.length;
      continue;
    }

    // Append to buffer
    const newBuffer = new Uint8Array(buffer.length + value.length);
    newBuffer.set(buffer);
    newBuffer.set(value, buffer.length);
    buffer = newBuffer;

    // Try to parse NDJSON lines from buffer
    while (!headerComplete) {
      const newlineIdx = buffer.indexOf(10); // '\n'
      if (newlineIdx === -1) break;

      const lineBytes = buffer.slice(0, newlineIdx);
      buffer = buffer.slice(newlineIdx + 1);

      const line = decoder.decode(lineBytes).trim();
      if (!line) continue;

      try {
        const message = JSON.parse(line);
        if (message.type === 'progress') {
          onProgress?.(message.message);
        } else if (message.type === 'error') {
          throw new Error(message.message);
        } else if (message.type === 'complete') {
          headerComplete = true;
          // Remaining buffer is start of MP4 binary
          if (buffer.length > 0) {
            mp4Chunks.push(buffer);
            mp4BytesRead += buffer.length;
          }
        }
      } catch (e) {
        if (e instanceof Error && e.message !== line) throw e;
        console.warn('[exportScenarioMp4Stream] Failed to parse NDJSON line:', line);
      }
    }
  }

  if (!headerComplete) {
    throw new Error('Stream ended without completion marker');
  }

  // Combine MP4 chunks into a single blob
  const mp4Data = new Uint8Array(mp4BytesRead);
  let offset = 0;
  for (const chunk of mp4Chunks) {
    mp4Data.set(chunk, offset);
    offset += chunk.length;
  }

  return new Blob([mp4Data], { type: 'video/mp4' });
}

/**
 * Export multiple scenarios as a single concatenated MP4 video.
 */
export async function exportScenariosMp4Concat(
  scenarioIds: string[],
  options?: { burn_telop?: boolean },
): Promise<Blob> {
  const { data } = await apiClient.post(
    '/generation/scenarios/export-mp4-concat',
    { scenario_ids: scenarioIds, ...(options || {}) },
    { responseType: 'blob', timeout: 600000 },
  );
  return data;
}

export interface AutoGenerateResult {
  scenario_id: string;
  frames_generated: number;
  frames_failed: number;
  narrations_generated: number;
  narrations_failed: number;
  scenes: Array<{
    scene_id: string;
    order_number: number;
    frame_generated: boolean;
    narration_generated: boolean;
    frame_error?: string;
    narration_error?: string;
    image_url?: string;
  }>;
}

/**
 * Auto-generate frames and narrations for all scenes in a scenario.
 * Used by Express Mode.
 */
export async function autoGenerateScenarioAssets(
  scenarioId: string,
  options?: { voice_id?: string; aspect_ratio?: string; resolution?: string; model?: string; generate_endcard?: boolean },
): Promise<AutoGenerateResult> {
  const { data } = await apiClient.post<AutoGenerateResult>(
    `/generation/scenarios/${scenarioId}/auto-generate`,
    options || {},
    { timeout: 600000 },  // 10 min timeout
  );
  return data;
}

/**
 * ストーリーボード（絵コンテ）生成パラメータ
 */
export interface GenerateStoryboardsOptions {
  count?: number;              // 1〜5（GPT Image 2 の n）
  model?: string;              // 'gpt-image-2.5-flare-auto' か、テキスト駆動動画モデル時は 'gemini-nano-banana-2.1' (Gemini 標準)
  // 絵コンテ画像の解像度 (5:8 固定)。'1280px'=標準 800×1280（既定・従来の固定値）/
  // '2K'=1280×2048。未指定時は backend が '1280px' にフォールバックする。
  image_size?: '1280px' | '2K';
  // 各コマに描かせるフレーム数の指示モード。
  //   'first_and_last': スタート/エンドの 2 フレーム（既定・従来動作）
  //   'first_only':     スタートフレームのみ
  frame_mode?: 'first_and_last' | 'first_only';
  additional_prompt?: string;
  casting_gender?: string;
  casting_age?: number | null;
  // 顔を描かないモード。Seedance 等を動画モデルに使う場合、絵コンテ段階から
  // 顔の写らない構図を指示しておかないと動画側で追従しきれないので、フロント
  // から明示的に渡す。
  avoid_faces?: boolean;
  // 絵コンテをイラスト(手書き風)で描くモード。avoid_faces とは排他。参照画像に実在の
  // 顔写真を出さず、完成動画は実写で生成させる (Seedance のサングラス問題の代替手段)。
  illustration_storyboard?: boolean;
  // 映像スタイル。ビルトイン key または DB 属性ツリー由来 key (OEM 独自含む) の文字列。
  // 未指定時は backend がシナリオ作成時の generation_metadata.visual_style にフォールバックする。
  visual_style?: string;
  shooting_method?: string;
  // ブランドロゴを参照画像に追加するか。studio 静止画の include_logo と同じ意図。
  include_logo?: boolean;
  // フロントで選択した brand_logos.id (1 つだけ採用)。未指定なら従来通り全件採用。
  logo_id?: string;
  // 商品メイン画像を参照画像に追加するか。既定は false (送らない)。
  // ※静止画(絵コンテ)生成では未使用。動画生成側でのみ作用する。
  include_product_image?: boolean;
  // 静止画(絵コンテ)生成の参照画像にする「選択中の素材 (selectedAssets)」のURL配列。
  // キーを送ると backend は商品テーブルの自動取得を置き換える。空配列は「参照なし」を意味し
  // 尊重される。キー自体を送らない(旧クライアント)場合のみ従来の商品自動取得にフォールバック。
  selected_asset_urls?: string[];
  // 選択素材の URL + 素材ID。backend が素材名を解決して参照画像ラベルに使う。
  // selected_asset_urls と併送する (後方互換のため URL 配列も残す)。
  selected_assets?: { url: string; material_id?: string }[];
}

export interface GenerateStoryboardsResult {
  batch_id: string;
  scenario_id: string;
  storyboards: Storyboard[];
}

/**
 * ストーリーボード生成ジョブを作成し job_id を返す（POST のみ）。
 *
 * ポーリングはあえて含めない。絵コンテ生成は数分かかるため、ポーリングを
 * このコールの内側に閉じ込めると、シナリオ切替で呼び出しコンポーネントが
 * unmount されたときに進捗を追えなくなる（生成が「切れた」ように見える）。
 * 完了待ちは呼び出し側で activeJobs + waitForJobCompletion を使い、画面遷移を
 * またいで追跡できる場所（GenerationContext）で行う。
 */
export async function createStoryboardJob(
  scenarioId: string,
  options?: GenerateStoryboardsOptions,
): Promise<{ job_id: string }> {
  const body: Record<string, unknown> = { scenario_id: scenarioId };
  if (options?.count) body.count = options.count;
  if (options?.model) body.model = options.model;
  if (options?.image_size) body.image_size = options.image_size;
  if (options?.frame_mode) body.frame_mode = options.frame_mode;
  if (options?.additional_prompt) body.additional_prompt = options.additional_prompt;
  if (options?.casting_gender) body.casting_gender = options.casting_gender;
  if (typeof options?.casting_age === 'number') body.casting_age = options.casting_age;
  if (options?.avoid_faces) body.avoid_faces = true;
  if (options?.illustration_storyboard) body.illustration_storyboard = true;
  if (options?.visual_style) body.visual_style = options.visual_style;
  if (options?.shooting_method !== undefined) body.shooting_method = options.shooting_method;
  if (options?.include_logo) body.include_logo = true;
  if (options?.logo_id) body.logo_id = options.logo_id;
  if (options?.include_product_image) body.include_product_image = true;
  // キーの有無で意味を持たせる: 空配列も送って「参照なしで生成」を表現できるようにする。
  if (options?.selected_asset_urls !== undefined) body.selected_asset_urls = options.selected_asset_urls;
  if (options?.selected_assets !== undefined) body.selected_assets = options.selected_assets;

  const { data } = await apiClient.post<{ job_id: string }>(
    '/jobs/storyboard/generate',
    body,
  );
  return data;
}

/**
 * ユーザー画像をストーリーボード候補としてアップロード（1 枚ずつ）。
 * クレジット消費は無し。生成と同じ scenario_storyboards 行として末尾 letter で追加される。
 */
export async function uploadStoryboard(scenarioId: string, file: File): Promise<Storyboard> {
  const form = new FormData();
  form.append('file', file);
  const { data } = await apiClient.post<{ storyboard: Storyboard }>(
    `/generation/scenarios/${scenarioId}/storyboards/upload`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return data.storyboard;
}

/**
 * ストーリーボード候補一覧を取得
 */
export async function listStoryboards(scenarioId: string): Promise<Storyboard[]> {
  const { data } = await apiClient.get<{ storyboards: Storyboard[] }>(
    `/generation/scenarios/${scenarioId}/storyboards`,
  );
  return data.storyboards || [];
}

/**
 * ストーリーボード候補を 1 枚選択（動画化対象に設定）
 */
export async function selectStoryboard(scenarioId: string, storyboardId: string): Promise<Storyboard> {
  const { data } = await apiClient.post<Storyboard>(
    `/generation/scenarios/${scenarioId}/storyboards/${storyboardId}/select`,
  );
  return data;
}

/**
 * ストーリーボード候補を削除
 */
export async function deleteStoryboard(scenarioId: string, storyboardId: string): Promise<void> {
  await apiClient.delete(`/generation/scenarios/${scenarioId}/storyboards/${storyboardId}`);
}

/** シナリオ全体の境界丸め (BGM の小節頭に合わせる) の 1 シーン分 (backend motion_boundaries.py)。 */
export interface MotionBoundaryRoundingItem {
  scene_id: string;
  order_number: number | null;
  /** 動画素材付き・ブランドカット・静止画など、尺を動かさないシーン */
  fixed: boolean;
  motion: boolean;
  /** 合成の現在の尺 (秒) */
  before_sec: number;
  /** 現在の予定尺 (scenes.video_duration_seconds・整数) */
  planned_sec: number | null;
  /** 小節丸めの厳密な尺 (秒) */
  exact_sec: number;
  /** 書き戻す予定尺 (整数) */
  after_sec: number | null;
  /** 書き戻した後の合成の尺 (秒。固定シーンは before_sec と同じ) */
  after_resolved_sec?: number;
  /** このシーンの終端境界のずれ (秒) */
  shift_sec: number;
  changed: boolean;
}

export interface MotionBoundaryRoundingResult {
  ok: boolean;
  reason?: string;
  /** apply=true のときだけ。'stale' = プレビュー後に入力が変わったので書かなかった (再プレビューが要る) */
  status?: 'applied' | 'skipped' | 'stale';
  gridKind: 'bars' | 'beats' | 'metronome';
  tempoBpm: number | null;
  bgmStartSec: number;
  note: string;
  items: MotionBoundaryRoundingItem[];
  changed: number;
  total_sec: number;
  /** 書き戻した後の合成の総尺 (整数化の誤差は 0.5 秒未満に抑える) */
  total_after_sec?: number;
  /** 計画の入力の指紋。適用時に渡すと、入力が変わっていれば backend は書かずに status='stale' を返す */
  fingerprint?: string;
}

/**
 * BGM の小節頭に合わせてシーンの予定尺を丸める (POST /scenarios/:id/motion/round-boundaries・ポイント消費なし)。
 * apply=false はプレビュー (書き戻さない)。対象外 (storyboard・採用 BGM 無し) は 400 で error メッセージが返る。
 */
export async function roundScenarioMotionBoundaries(
  scenarioId: string,
  apply: boolean,
  fingerprint?: string,
): Promise<MotionBoundaryRoundingResult> {
  const { data } = await apiClient.post(
    `/generation/scenarios/${scenarioId}/motion/round-boundaries`,
    fingerprint ? { apply, fingerprint } : { apply },
  );
  return data;
}

/**
 * 生成済みモーションシーンの BGM 同期の鮮度を取得する (GET /scenarios/:id/motion/sync-status・読み取りのみ・
 * ポイント消費なし。docs/features/brand-cut-motion-graphics-plan.md §10.4 / §15.4)。
 * 戻り値は scene_id → {stale, reason}。対象 (生成済みのモーションシーン) 以外は含まれない。
 */
export async function fetchScenarioMotionSyncStatus(
  scenarioId: string,
): Promise<Record<string, import('./types').MotionSyncStatus>> {
  const { data } = await apiClient.get<{ scenes?: Record<string, import('./types').MotionSyncStatus> }>(
    `/generation/scenarios/${scenarioId}/motion/sync-status`,
  );
  return data?.scenes ?? {};
}

// ============================================================================
// 全編モーショングラフィックス (Phase 2e・docs/features/brand-cut-motion-graphics-plan.md §10.5)
// ============================================================================

/** 台本に渡す曲の要約 (backend motion_song.summarize_for_prompt)。metronome / 解析失敗は null (台本は秒ベース)。 */
export interface MotionSongSummary {
  gridKind: 'bars' | 'beats';
  songSec: number;
  bpm: number | null;
  barSec: number;
  leadInSec: number;
  barCount: number;
  allowedBars: number[];
  sectionBars: number[];
  peakBars: number[];
  /** 小節数を想定尺 (と全編 60 秒) で頭打ちにしたときだけ: 台本に使う尺の上限 (秒) と曲全体の小節数 (motion_song.cap_for_duration) */
  capSec?: number;
  songBarCount?: number;
}

/** 全編モーションのシナリオ生成で先に決める BGM (生成 / アップロード / なし)。 */
/**
 * BGM を先に決めるモーダルの選択 (計画 md §10.5 / §10.9)。beat_sync = 「BGM の拍に合わせる」(既定 true)。false なら台本を
 * 秒ベース (小節の指示なし) で書き、尺の割付もナレーションの長さで決める。BGM なし (none) では拍が無いので持たない。
 */
/**
 * 生成タブの曲の指定 (backend parse_motion_bgm_song)。テンプレート (template_key) のときはボーカルなし・詳細なし固定。
 * 詳細 (genre / mood / bpm / musical_key) は「曲の詳細を設定する」のときだけ送る。bpm は 60〜180 の整数。
 */
export type MotionBgmSong =
  | { template_key: string }
  | { vocal: boolean; genre?: string; mood?: string; bpm?: number | null; musical_key?: string };

export type MotionBgmChoice =
  | { mode: 'generate'; provider: BgmProvider; auto_continue: boolean; beat_sync: boolean; song?: MotionBgmSong }
  | { mode: 'upload'; material_id: string; beat_sync: boolean }
  | { mode: 'none' };

export type MotionPrepareResponse = {
  scenario_id: string;
  /** mode=generate のときだけ。BGM 生成ジョブ (GET /jobs/<id>) */
  bgm_job_id?: string;
  /** mode=upload / none のときだけ。シナリオ生成ジョブ (generate-status/<id>) */
  scenario_job_id?: string;
  song?: MotionSongSummary | null;
  /** 解析サービスの失敗 (拍が取れない曲とは別)。台本は秒ベースで書き、復旧後に「計画を更新」で合わせ直せる */
  song_error?: string | null;
};

/**
 * 全編モーションのシナリオを BGM 先行で始める (POST /scenarios/motion/prepare)。
 * シェル (status='generating') を作り、BGM 生成ジョブまたはシナリオ生成ジョブを載せる。本数は 1 に固定される。
 * 400 (engine が motion でない・歌モノ・不正な bgm) / 402 / 404 / 422 はシェルもジョブも作らない。
 */
export async function prepareMotionScenario(
  payload: Record<string, unknown>,
  bgm: MotionBgmChoice,
): Promise<MotionPrepareResponse> {
  const { data } = await apiClient.post<MotionPrepareResponse>('/generation/scenarios/motion/prepare', { ...payload, bgm });
  return data;
}

export type MotionGenerateFromBgmResponse = {
  scenario_id: string;
  scenario_job_id: string;
  song?: MotionSongSummary | null;
  /** 解析サービスの失敗 (拍が取れない曲とは別) */
  song_error?: string | null;
  /** 409 (既に投入済み) を成功扱いにしたとき true。返ってきた scenario_job_id を待てばよい */
  alreadyStarted?: boolean;
};

/**
 * シェルに紐づいた BGM でシナリオを生成する (POST /scenarios/<id>/motion/generate-from-bgm)。
 * withoutBgm=true は BGM なしで進める (シェルを bgm_excluded にして秒ベースの台本にする)。
 * 409 (既に投入済み) は返ってきた scenario_job_id を成功として返す。
 */
export async function generateMotionScenarioFromBgm(
  scenarioId: string,
  opts?: { withoutBgm?: boolean },
): Promise<MotionGenerateFromBgmResponse> {
  try {
    const { data } = await apiClient.post<MotionGenerateFromBgmResponse>(
      `/generation/scenarios/${scenarioId}/motion/generate-from-bgm`,
      opts?.withoutBgm ? { without_bgm: true } : {},
    );
    return data;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.status === 409) {
      const body = err.response.data as { scenario_job_id?: string } | undefined;
      if (body?.scenario_job_id) {
        return { scenario_id: scenarioId, scenario_job_id: body.scenario_job_id, alreadyStarted: true };
      }
    }
    throw err;
  }
}

/**
 * 全編モーションの BGM をモーション専用プロンプトで作り直す (POST /scenarios/<id>/bgm-async {motion_prompt: true})。
 * 自動でシナリオ生成には続かない (完了後に generateMotionScenarioFromBgm を呼ぶ)。ジョブ id を返す。
 */
export async function regenerateMotionBgm(scenarioId: string, provider?: BgmProvider): Promise<string> {
  const body: Record<string, unknown> = { motion_prompt: true };
  if (provider) body.provider = provider;
  const { data } = await apiClient.post<{ job_id: string }>(`/generation/scenarios/${scenarioId}/bgm-async`, body);
  return data.job_id;
}

/** 曲に収まらないときの台本短縮の目安 (backend motion_plan)。 */
export interface MotionPlanAdvice {
  shortenSec: number;
  extendBgmSec: number;
  scenes: Array<{ sceneId: string; order: number | null; narrationSec: number; minSec: number; shortenSec: number }>;
  /** 利用者向け文言 */
  message: string;
}

export interface MotionPlanItem {
  sceneId: string;
  kind: 'body' | 'brand_start' | 'brand_end';
  /** 小節の格子が無い計画 (gridKind 'seconds'・BGM なし / 拍が取れない曲) では null */
  startBar: number | null;
  bars: number | null;
  startSec: number;
  sec: number;
  endSec: number;
  planVersion?: string;
}

export type MotionPlan =
  | {
      planned: true;
      planVersion: string;
      plannedAt: string;
      /** 'seconds' = 小節の格子を使わない計画 (BGM なし / 拍が取れない曲 / 拍に合わせない設定・2026-09-30) */
      gridKind: 'bars' | 'beats' | 'seconds';
      /** BGM なしの計画では null */
      bgmMaterialId: string | null;
      analysisMethod?: string | null;
      analyzedAt?: string | null;
      /** 曲の実尺。BGM なしの計画では null */
      songSec: number | null;
      /** 平均小節長。格子の無い計画では null */
      barSec: number | null;
      bgmEndSec?: number | null;
      fade?: unknown;
      /** 小節頭で割り付けられず緩めた段階 (拍 / 秒)。緩めていなければ null */
      relaxed?: 'beats' | 'seconds' | null;
      /** 緩めた / 曲からはみ出したときの利用者向けの注意 (生成は止めない) */
      relaxNotice?: string | null;
      /** ナレーションの合計が曲より長く、曲の後ろへはみ出した秒数 (BGM が先に終わる) */
      overflowSec?: number;
      items: MotionPlanItem[];
      notes?: string[];
    }
  | {
      planned: false;
      reason: string;
      fallback?: boolean;
      advice?: MotionPlanAdvice;
      decidedAt?: string;
    };

/** 全編 1 本で生成した素材の記録 (シナリオの generation_metadata.motion_whole・Phase 2f §10.6)。 */
export interface MotionWholeInfo {
  materialId: string;
  planVersion: string | null;
  createdAt: string | null;
  jobId?: string | null;
}

/** 全編の生成の途中経過の 1 版 (初稿 / 採点の指摘を反映した修正)。 */
export interface MotionWholePreviewVersion {
  round: number;
  kind: 'draft' | 'revise';
  /** 試写のコンタクトシート (署名付き URL・24 時間)。保存に失敗した版は null */
  contactSheetUrl: string | null;
  scores: Record<string, number>;
  average: number;
  passed: boolean;
  summary: string;
  at?: string;
}

export interface MotionWholePreviews {
  jobId: string;
  startedAt?: string;
  finishedAt?: string;
  status: 'running' | 'succeeded' | 'failed';
  /** 本描画に採用した版 (成功時) */
  adoptedRound?: number | null;
  versions: MotionWholePreviewVersion[];
}

export interface MotionPlanResponse {
  engine: 'motion' | null;
  motion_plan: MotionPlan | null;
  check: { hasPlan: boolean; matches: boolean; reason: string | null };
  /** 全編 1 本の素材 (Phase 2f)。未生成は null。古い backend では欠ける */
  whole?: MotionWholeInfo | null;
  /** 料金 (Phase 2f)。whole = 全編の料金 (100 + 50 × (本編 − 1))・wholeSpeed = 全編のスピードの料金 (1 本の定額・古い backend は欠ける)・
   *  scene = 1 シーンの料金。null = レート未設定 */
  credits?: { whole: number | null; wholeSpeed?: number | null; scene: number | null };
  /** 計画の総尺 (秒)。計画が無ければ null */
  durationSec?: number | null;
  /** 全編を 1 本で作れるか (計画あり かつ 総尺 ≤ 60 秒) */
  wholeAllowed?: boolean;
  /** 全編の生成の途中経過 (版ごとの試写のコマと採点)。未生成・古い backend では欠ける */
  previews?: MotionWholePreviews | null;
  /** BGM の拍・小節に合わせるか (generation_metadata.motion_beat_sync・既定 true)。古い backend では欠ける */
  beatSync?: boolean;
  /**
   * 拍の設定を生成前 (BGM モーダル) に決めたか (§10.9)。true ならカードは読み取り専用の表示 (変更は台本の作り直し)。
   * 「後で付ける・なし」で作って後から BGM を付けたシナリオは false (従来のトグルで決める)。古い backend では欠ける
   */
  beatSyncFixed?: boolean;
  scenes: Array<{
    scene_id: string;
    order: number | null;
    kind: 'body' | 'brand_start' | 'brand_end';
    motion_bars: number | null;
    video_duration_seconds: number | null;
    item: MotionPlanItem | null;
    scene_plan: unknown;
  }>;
}

/** 全編モーションの計画を読む (GET /scenarios/<id>/motion/plan・読み取りのみ)。 */
export async function fetchScenarioMotionPlan(scenarioId: string): Promise<MotionPlanResponse> {
  const { data } = await apiClient.get<MotionPlanResponse>(`/generation/scenarios/${scenarioId}/motion/plan`);
  return data;
}

export interface MotionPlanChange {
  sceneId: string;
  order: number | null;
  before: number | null;
  after: number;
}

export type MotionReplanResult =
  | { ok: true; planned: true; planVersion: string; changed: MotionPlanChange[]; plan?: MotionPlan }
  | { ok: true; planned: false; reason: string; changed: MotionPlanChange[] }
  | { ok: false; planned: false; reason: string; advice?: MotionPlanAdvice; changed: MotionPlanChange[] };

/**
 * 計画を解き直す (POST /scenarios/<id>/motion/replan・ポイント消費なし)。BGM があれば小節に合わせ、無ければ
 * ナレーションの長さと予定尺で秒に割り付ける (2026-09-30)。400 = engine が motion でない。
 */
export async function replanScenarioMotion(scenarioId: string): Promise<MotionReplanResult> {
  const { data } = await apiClient.post<MotionReplanResult>(`/generation/scenarios/${scenarioId}/motion/replan`, {});
  return data;
}

/** 全編モーションの設定 (BGM の拍に合わせるか) を書き、尺の割付を解き直す (PUT /motion/settings)。 */
export async function updateScenarioMotionSettings(
  scenarioId: string,
  settings: { beat_sync: boolean },
): Promise<{ beatSync: boolean; replan: MotionReplanResult }> {
  const { data } = await apiClient.put<{ beatSync: boolean; replan: MotionReplanResult }>(`/generation/scenarios/${scenarioId}/motion/settings`, settings);
  return data;
}

// ── 全編を 1 本で生成して計画の境界で分割する (Phase 2f・docs/features/brand-cut-motion-graphics-plan.md §10.6) ──

export interface MotionGenerateWholeResponse {
  job_id: string;
  status: string;
  credits_required: number;
  scene_count: number;
  duration_sec: number;
  plan_version: string;
}

/**
 * 全編モーションを 1 本で生成し、計画の境界で分割して各シーンの素材にする (POST /scenarios/<id>/motion/generate-whole)。
 * 受付時に replan する。400 (全編モーションでない / 60 秒超 など) / 422 (計画が作れない・advice / retryable) /
 * 402 / 409 (実行中・job_id 付き) / 503 は呼び出し側で扱う (classifyWholeMotionError)。
 */
export async function generateWholeScenarioMotion(
  scenarioId: string,
  options: { quality?: 'speed' | 'draft' | 'loop'; model?: MotionModel } = {},
): Promise<MotionGenerateWholeResponse> {
  // quality (§10.10.1): 'speed' = スピード (1 本の定額) / 'draft' = 標準 / 'loop' = ダメ出し (採点と修正を繰り返す)。省略時は backend の既定
  // model: 初稿の AI モデル (features/motion/model)。省略時は backend の既定
  const { data } = await apiClient.post<MotionGenerateWholeResponse>(
    `/generation/scenarios/${scenarioId}/motion/generate-whole`,
    {
      ...(options.quality ? { quality: options.quality } : {}),
      ...(options.model ? { model: options.model } : {}),
    },
  );
  return data;
}

export interface MotionReviseWholeResponse {
  job_id: string;
  status: string;
  credits_required: number;
}

/** 全編の「AI で修正」(POST /scenarios/<id>/motion/revise・定額)。直した全編を同じ境界で切り直して各シーンを置き換える。 */
export async function reviseWholeScenarioMotion(scenarioId: string, instruction: string): Promise<MotionReviseWholeResponse> {
  const { data } = await apiClient.post<MotionReviseWholeResponse>(
    `/generation/scenarios/${scenarioId}/motion/revise`,
    { instruction },
  );
  return data;
}

/** 全編ジョブ (GET /jobs/<id>) の result_data の 1 シーン分。scenes の行は backend が更新済み。 */
export interface MotionWholeSceneResult {
  scene_id: string;
  order: number | null;
  kind: 'body' | 'brand_start' | 'brand_end';
  material_id: string | null;
  video_url: string | null;
  thumbnail_url: string | null;
  actual_duration_seconds: number | null;
  planned_seconds: number | null;
}

export interface MotionWholeJobResult {
  scenario_id: string;
  /** = 全編の素材 */
  material_id: string | null;
  whole_material_id: string | null;
  engine: 'motion';
  video_url: string | null;
  thumbnail_url: string | null;
  duration_sec: number | null;
  plan_version: string | null;
  scenes: MotionWholeSceneResult[];
  output?: Record<string, unknown>;
  adopted_round?: number | null;
  adopted_passed?: boolean | null;
  tokens?: boolean;
  revised_from?: string | null;
}

// ============================================================================
// 声の台本 (script_contract='motion-v2') と複数話者の声 (Phase 2g・docs/features/brand-cut-motion-graphics-plan.md §10.7)
// ============================================================================

/** 声のプロバイダ (ナレーションと同じ 3 種) */
export type VoiceProvider = 'elevenlabs' | 'fish' | 'gemini';

/**
 * 話者に割り当てた声。style は読み方 (プロバイダの narration_styles の key か 'custom')。
 * 'custom' のときは style_text に自由記述を付けられる。検証違反は PUT が 400 VOICE_MAP_INVALID。
 */
export interface VoiceAssignment {
  provider: VoiceProvider;
  voice_id: string;
  style?: string | null;
  style_text?: string | null;
}

export type VoiceSpeakerKind = 'narration' | 'character' | 'actor' | 'label';

/** 今の台本に現れる話者 1 人分 (GET /voice-map の speakers)。speaker_id は narration / character:<key> / actor:<kind>:<id> / label:<label> */
export interface VoiceSpeaker {
  speaker_id: string;
  kind: VoiceSpeakerKind;
  label: string;
  key?: string | null;
  actor_id?: string | null;
  actor_kind?: string | null;
  used_in_scene_ids: string[];
  /** 解決済みの声 (ナレーターは voice_map に無ければ既存のナレーション設定)。未割り当ては null */
  voice: VoiceAssignment | null;
  /** ナレーターの声が既存のナレーション設定からの引き継ぎ (未保存の既定) のとき true */
  voice_is_default?: boolean;
}

/** シーンのナレーションの鮮度 (GET /voice-map の scene_status・TTS の行があるシーンだけ)。needsRegenerate が null は判定なし */
export interface VoiceSceneStatus {
  needsRegenerate: boolean | null;
  needsRegenerateReason: string | null;
}

export interface ScenarioVoiceMapResponse {
  voice_map: Record<string, VoiceAssignment>;
  speakers: VoiceSpeaker[];
  /** シーン id → ナレーションの鮮度 (TTS の行があるシーンだけ・旧 BE は無い) */
  scene_status: Record<string, VoiceSceneStatus>;
}

function normalizeVoiceMapResponse(data: Partial<ScenarioVoiceMapResponse> | null | undefined): ScenarioVoiceMapResponse {
  return {
    voice_map: data?.voice_map ?? {},
    speakers: Array.isArray(data?.speakers) ? data.speakers : [],
    scene_status: data?.scene_status && typeof data.scene_status === 'object' ? data.scene_status : {},
  };
}

export const scenarioVoiceMapQueryKey = (scenarioId: string) => ['scenario-voice-map', scenarioId] as const;

/** 話者と声の対応表と、今の台本に現れる話者の一覧 (GET /scenarios/<id>/voice-map・読み取りのみ)。 */
export async function fetchScenarioVoiceMap(scenarioId: string): Promise<ScenarioVoiceMapResponse> {
  const { data } = await apiClient.get<ScenarioVoiceMapResponse>(`/generation/scenarios/${scenarioId}/voice-map`);
  return normalizeVoiceMapResponse(data);
}

/**
 * 話者と声の対応表を保存する (PUT /scenarios/<id>/voice-map・部分マージ)。送った話者だけが変わり、値を null にすると
 * その話者の割り当てを外す。応答は GET と同じ (保存後の全体)。検証違反は 400 VOICE_MAP_INVALID。
 */
export async function updateScenarioVoiceMap(
  scenarioId: string,
  voiceMap: Record<string, VoiceAssignment | null>,
): Promise<ScenarioVoiceMapResponse> {
  const { data } = await apiClient.put<ScenarioVoiceMapResponse>(
    `/generation/scenarios/${scenarioId}/voice-map`,
    { voice_map: voiceMap },
  );
  return normalizeVoiceMapResponse(data);
}

export interface GenerateVoiceLinesResponse {
  job_id: string;
  scenes: Array<{ scene_id: string; order: number | null; providers: string[]; credits: number }>;
  credits_total: number;
}

/**
 * ナレーションを生成する (POST /scenarios/<id>/voice-lines/generate・1 ジョブ)。scene_ids 省略で全シーン。
 * scene_languages はシーンごとの発音言語 (無いシーンは language・どちらも無ければ ja)。
 * 400 VOICE_SPEAKER_UNASSIGNED {speakers} / VOICE_SOURCE_MIXED {scenes} / VOICE_LINES_EMPTY・402・409 は呼び出し側で扱う。
 */
export async function generateScenarioVoiceLines(
  scenarioId: string,
  payload: { scene_ids?: string[]; language?: 'ja' | 'en'; scene_languages?: Record<string, 'ja' | 'en'> } = {},
): Promise<GenerateVoiceLinesResponse> {
  const { data } = await apiClient.post<GenerateVoiceLinesResponse>(
    `/generation/scenarios/${scenarioId}/voice-lines/generate`,
    payload,
  );
  return data;
}

export interface RetakeVoiceLineResponse {
  job_id: string;
  credits: number;
}

/**
 * 1 行だけ撮り直す (POST /scenarios/<id>/scenes/<scene_id>/voice-lines/retake)。
 * 409 VOICE_LINES_CHANGED {changed_line_ids, reason} は「このシーンの声を生成し直す」へ誘導する。
 */
export async function retakeSceneVoiceLine(
  scenarioId: string,
  sceneId: string,
  payload: { line_id: string; language?: 'ja' | 'en' },
): Promise<RetakeVoiceLineResponse> {
  const { data } = await apiClient.post<RetakeVoiceLineResponse>(
    `/generation/scenarios/${scenarioId}/scenes/${sceneId}/voice-lines/retake`,
    payload,
  );
  return data;
}

/** 分割位置の調整モーダルの 1 行 (分割前音声の中の並び順)。 */
export interface VoiceLinesSplitLine {
  line_id: string;
  scene_id: string;
  /** 今のシーン番号。削除済みシーンは null */
  order: number | null;
  /** 話者の表示名 (ナレーター / 登場人物名) */
  speaker_label: string;
  /** まとめて読み上げた本文 */
  text: string;
  /** 今の区切りで決まる範囲 [開始秒, 終了秒] (音声全体を隙間なく覆う) */
  range_sec: [number, number];
  /** 'timestamps' | 'whisper' | 'proportional' | 'manual' */
  alignment?: string | null;
  /** 削除済みシーン / 台本から消えた行 (区間は残るが割り当てない) */
  missing: boolean;
  /** 今のシーンの声がこの塊の音声から作られたものか (別に生成し直したシーンは false) */
  assigned: boolean;
}

export interface VoiceLinesSplitChunk {
  full_material_id: string;
  chunk_index: number;
  duration_seconds: number;
  preview_url?: string | null;
  source_url?: string | null;
  storage_bucket?: string | null;
  storage_path?: string | null;
  /** 振幅ピーク (0〜1)。peaks_per_sec 個で 1 秒 */
  peaks: number[];
  peaks_per_sec: number;
  lines: VoiceLinesSplitLine[];
}

export interface VoiceLinesSplitView {
  batch_id: string | null;
  chunks: VoiceLinesSplitChunk[];
}

export const voiceLinesSplitQueryKey = (scenarioId: string) => ['voice-lines-split', scenarioId] as const;

/** 最新のまとめて TTS の分割前音声 (波形) と、各行の今の区切りを取得する (GET /scenarios/<id>/voice-lines/split)。 */
export async function fetchVoiceLinesSplit(scenarioId: string): Promise<VoiceLinesSplitView> {
  const { data } = await apiClient.get<VoiceLinesSplitView>(`/generation/scenarios/${scenarioId}/voice-lines/split`);
  return { batch_id: data?.batch_id ?? null, chunks: Array.isArray(data?.chunks) ? data.chunks : [] };
}

export interface VoiceLinesResplitResult {
  scenes: Array<{
    scene_id: string;
    order: number | null;
    material_id: string;
    duration_seconds: number | null;
    voice_lines: VoiceLineTiming[];
  }>;
  /** 切り直せなかったシーン (台本が変わっている等) */
  failed: Array<{ scene_id: string; order: number | null; error: string; error_code?: string }>;
  /** 区切りが変わらず切り直さなかった行数 */
  unchanged: number;
  plan?: VoiceLinesJobResult['plan'];
}

/**
 * 分割前音声 1 本を指定の境界 (行数 - 1 個の秒・昇順) で切り直し、境界が動いた行を含むシーンの声を組み立て直す
 * (POST /scenarios/<id>/voice-lines/resplit)。TTS を呼ばないのでポイントは消費しない。
 */
export async function resplitVoiceLines(
  scenarioId: string,
  payload: { full_material_id: string; cuts: number[] },
): Promise<VoiceLinesResplitResult> {
  const { data } = await apiClient.post<VoiceLinesResplitResult>(`/generation/scenarios/${scenarioId}/voice-lines/resplit`, payload);
  return { ...data, scenes: data?.scenes ?? [], failed: data?.failed ?? [], unchanged: data?.unchanged ?? 0 };
}

/** 声の素材 / ジョブの結果の 1 行 (連結後の開始 / 終了秒)。 */
export interface VoiceLineTiming {
  line_id: string;
  index: number;
  speaker: { kind: string; key?: string | null; label: string };
  text: string;
  voice?: { provider: string; voice_id: string; style?: string | null };
  startSec: number;
  endSec: number;
}

/** 声のジョブ (generate_voice_lines / retake_voice_line) の result_data。 */
export interface VoiceLinesJobResult {
  scenes?: Array<{
    scene_id: string;
    order: number | null;
    material_id: string;
    duration_seconds: number | null;
    voice_lines: VoiceLineTiming[];
    input_fingerprint?: string;
    audio_version?: string;
  }>;
  /** 失敗したシーン (ジョブは全シーンが失敗しても completed で終わるので必ず見る)。clips は生成できた行のクリップ */
  failed?: Array<{ scene_id: string; order: number | null; error: string; clips?: unknown }>;
  plan?: {
    status: 'ok' | 'advice' | 'skipped';
    advice?: string | MotionPlanAdvice | null;
    advice_detail?: MotionPlanAdvice | null;
    reasons?: string[] | null;
  };
  /** 撮り直しのジョブだけ: 撮り直した行 */
  retake_line_id?: string;
}

/** 旧台本の変換の試算 (dry_run) の結果。 */
export interface MotionV2ConvertDryRun {
  dry_run: true;
  convertible: boolean;
  max_lines?: number;
  scenes: Array<{ scene_id: string; order: number | null; narration_lines: number; existing_lines: number; lines: number }>;
  over_limit: Array<{ scene_id: string; order: number | null; lines: number }>;
  too_long: Array<{ scene_id: string; order: number | null; line: number; chars: number }>;
  invalid: Array<{ scene_id: string; order: number | null; error: string }>;
  warnings: Array<{ code: string; scene_id: string; order: number | null; message?: string }>;
}

export interface MotionV2ConvertResult {
  converted: true;
  script_contract: 'motion-v2';
  scenes: Array<{ scene_id: string; order: number | null; lines: number; dialogue_lines?: DialogueLine[] }>;
  warnings: MotionV2ConvertDryRun['warnings'];
}

/**
 * 旧契約のモーション台本を声の台本 (v2) に変換する (POST /scenarios/<id>/script/convert-motion-v2)。
 * dryRun=true は分割後の行数と警告だけ返す (何も書かない)。本実行は 422 (VOICE_LINES_OVER_LIMIT / VOICE_LINE_TOO_LONG /
 * VOICE_LINES_INVALID・何も書かない) / 409 を呼び出し側で扱う。
 */
export async function convertScenarioToMotionV2(scenarioId: string, dryRun: true): Promise<MotionV2ConvertDryRun>;
export async function convertScenarioToMotionV2(scenarioId: string, dryRun: false): Promise<MotionV2ConvertResult>;
export async function convertScenarioToMotionV2(
  scenarioId: string,
  dryRun: boolean,
): Promise<MotionV2ConvertDryRun | MotionV2ConvertResult> {
  const { data } = await apiClient.post<MotionV2ConvertDryRun | MotionV2ConvertResult>(
    `/generation/scenarios/${scenarioId}/script/convert-motion-v2`,
    { dry_run: dryRun },
  );
  if (!dryRun) notifyImportContentChanged({ scenarioId });
  return data;
}

/**
 * セリフとナレーションを入れ替える (POST /scenarios/<id>/script/swap-narration-dialogue・
 * docs/features/scenario-narration-dialogue-swap-plan.md §3.7)。dry_run=trueは対象数・話者集計・確認トークンだけ返す
 * (何も書かない)。実行 (dry_run=false) は確認済みプレビューの選択条件とconfirmationをそのまま送る。
 * 409 CONFIRMATION_STALE / 422 DIALOGUE_LINES_OVER_LIMIT・NARRATION_SPEAKER_REQUIRED・NARRATION_SPEAKER_INVALID /
 * 400 NOTHING_TO_SWAP / 409 NOT_SUPPORTED_MOTION_V2・SCENARIO_GENERATINGは呼び出し側で扱う。
 */
export async function swapNarrationDialogue(
  scenarioId: string,
  body: NarrationDialogueSwapRequest & { dry_run: true },
): Promise<NarrationDialogueSwapDryRun>;
export async function swapNarrationDialogue(
  scenarioId: string,
  body: NarrationDialogueSwapRequest & { dry_run: false },
): Promise<NarrationDialogueSwapResult>;
export async function swapNarrationDialogue(
  scenarioId: string,
  body: NarrationDialogueSwapRequest,
): Promise<NarrationDialogueSwapDryRun | NarrationDialogueSwapResult> {
  const { data } = await apiClient.post<NarrationDialogueSwapDryRun | NarrationDialogueSwapResult>(
    `/generation/scenarios/${scenarioId}/script/swap-narration-dialogue`,
    body,
  );
  if (!body.dry_run) notifyImportContentChanged({ scenarioId });
  return data;
}
